import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { message } from "antd";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { useNavigate } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import {
  GAME_PASS_DROPDOWN_LABELS,
  SIDER_TABS,
} from "../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { useCreateCampaign } from "../../../queries/game-pass/useCreateCampaign";
import { useCreateCampaignChallenge } from "../../../queries/game-pass/useCreateCampaignChallenge";
import { useCreateCampaignTask } from "../../../queries/game-pass/useCreateCampaignTask";
import { useUpdateCampaign } from "../../../queries/game-pass/useUpdateCampaign";
import { useScheduleCampaign } from "../../../queries/game-pass/useCampaignLifecycle";
import { useGetCampaignsList } from "../../../queries/game-pass/useGetCampaignsList";
import {
  buildCampaignScheduleConflictMessage,
  findCampaignScheduleConflict,
} from "../../../queries/game-pass/campaignScheduleConflict";
import {
  buildMonthlyDraftCampaignRequest,
  buildStreakResetHourPatch,
  mapMonthlyChallengeToChallengeRequest,
  mapMonthlyScheduleToUpdateCampaignRequest,
} from "../../../queries/game-pass/monthlyCampaignMappers";
import { mapMonthlyChallengeToTaskRequest } from "../../../queries/game-pass/monthlyTaskMapper";
import { Button, Input } from "../../shared";
import WizardPageHeader from "../shared/WizardPageHeader";
import WizardStepper from "../shared/WizardStepper";
import MonthlyChallengesStep from "./Challenges/MonthlyChallengesStep";
import MonthlySchedule from "./Schedule/MonthlySchedule";
import {
  MONTHLY_CHALLENGES_INITIAL_VALUES,
  hasAtLeastOneChallenge,
  type MonthlyChallengesValues,
} from "./monthlyChallengeData";
import {
  MONTHLY_SCHEDULE_INITIAL_VALUES,
  isMonthlyScheduleValid,
  resolveMonthlyAutoStartAnchor,
  type MonthlyScheduleValues,
} from "./monthlyScheduleData";

import styles from "./MonthlyChallenge.module.css";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

type MonthlyChallengeStepId = "overview" | "challenges" | "schedule";

// The three-step flow mirrors the Create Season wizard (the Figma reuses the
// same "Season Overview → Challenges → Schedule" chrome) and follows Season's
// draft-first lifecycle: Overview names the challenge and creates the campaign
// as a `draft` on Next, Challenges defines the tiered rewards, and Schedule
// (FE-2257) sets the start/end window. The final Create CTA attaches the
// challenge groups + tiered tasks, writes the real window, then transitions the
// draft out via the schedule API; Save as Draft persists the in-progress work
// at every step without scheduling (see `handleSaveAsDraft` / `handleCreate`).
const MONTHLY_CHALLENGE_STEPS: ReadonlyArray<{
  id: MonthlyChallengeStepId;
  label: string;
}> = [
  { id: "overview", label: AppStrings.SEASON_OVERVIEW },
  { id: "challenges", label: AppStrings.MONTHLY_CHALLENGE_STEP_CHALLENGES },
  { id: "schedule", label: AppStrings.SCHEDULE },
];

const STEP_PANEL_ID_PREFIX = "monthly-challenge-step-panel";

/**
 * Game Pass → Configuration → Monthly.
 *
 * Create Monthly Challenge wizard. Step 1 ("Season Overview") names the
 * challenge and creates the campaign as a `draft`; step 2 ("Challenges") defines
 * the challenges and their tiered rewards; step 3 ("Schedule") sets the
 * start/end window. The Schedule step's Create CTA attaches the challenge groups
 * + tiered tasks to the draft, writes the real window, then schedules it, and
 * redirects to the Monthly listing on success. Save as Draft persists the
 * admin's in-progress work at every step (draft campaign, the challenge groups +
 * tasks added so far, and the entered window on Schedule) without scheduling.
 */
const MonthlyChallenge: FC = () => {
  const navigate = useNavigate();
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;

  const [activeStep, setActiveStep] =
    useState<MonthlyChallengeStepId>("overview");
  const [name, setName] = useState("");
  const [challenges, setChallenges] = useState<MonthlyChallengesValues>(
    MONTHLY_CHALLENGES_INITIAL_VALUES,
  );
  const [schedule, setSchedule] = useState<MonthlyScheduleValues>(
    MONTHLY_SCHEDULE_INITIAL_VALUES,
  );
  // The campaign is created as a draft on Step 1 (mirrors Create Season), so
  // Step 3's Create only needs to attach groups/tasks + schedule it. `null`
  // until that draft POST returns.
  const [campaignId, setCampaignId] = useState<string | null>(null);
  // Which footer CTA fired the in-flight request, so the spinner sits on the
  // button the admin actually clicked (both share the same mutations).
  const [submittingAction, setSubmittingAction] = useState<
    "next" | "draft" | null
  >(null);
  // Tracks which challenge cards have already had their group/task persisted,
  // keyed by card id, so re-entering the persist step (retry after a partial
  // failure, or a second pass on Save/Next) resumes instead of creating
  // duplicates. A ref (not state) — it must survive re-renders without
  // triggering one, and it's only read inside the async flow.
  //
  // KNOWN LIMITATION (needs the admin campaign challenge/task edit + delete
  // API): this is create-only, with no tombstone for removals or edits. Until
  // that contract lands we close the data-integrity window by locking backward
  // navigation to the Challenges step once anything is persisted (see
  // `challengesPersisted`) — the admin can't return to delete/edit a card that
  // already has a backend record and orphan it.
  const persistedCardsRef = useRef<
    Map<string, { challengeId: string | null; taskDone: boolean }>
  >(new Map());
  // Set once any challenge group/task has been written to the backend. Locks
  // the Challenges step from further edits (see the limitation note above).
  const [challengesPersisted, setChallengesPersisted] = useState(false);

  const createCampaign = useCreateCampaign();
  const createChallenge = useCreateCampaignChallenge();
  const createTask = useCreateCampaignTask();
  const updateCampaign = useUpdateCampaign();
  const scheduleCampaign = useScheduleCampaign();
  // Unfiltered campaigns list drives the Schedule-step overlap guard (mirrors
  // Create Season using `useGetSeasonsList`). Fetched on mount so it's ready by
  // the time the admin reaches Schedule.
  const campaignsListQuery = useGetCampaignsList();

  // The currently ACTIVE monthly challenge the new draft auto-starts after —
  // drives the Schedule step's start auto-fill (active end + 1 min) and the
  // pre-filled 3-day end window (FE-2374). The draft we just created is excluded
  // so it can never anchor to itself.
  const autoStartAnchor = useMemo(
    () =>
      resolveMonthlyAutoStartAnchor(
        campaignsListQuery.data?.campaigns ?? [],
        campaignId,
      ),
    [campaignsListQuery.data?.campaigns, campaignId],
  );

  useEffect(() => {
    setActiveSidebarTab(
      SIDER_TABS?.GAME_PASS,
      GAME_PASS_DROPDOWN_LABELS.MONTHLY,
    );
  }, [setActiveSidebarTab]);

  const handleBack = useCallback(() => {
    navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_MONTHLY);
  }, [navigate]);

  // Mirrors Create Season's in-card "Back": step back through the wizard, and
  // exit to the Game Pass listing once past the first step.
  const handleHeaderBack = useCallback(() => {
    if (activeStep === "schedule") {
      // Once challenges are persisted, stepping back to Step 2 would let the
      // admin delete a card that already has a backend record and orphan it.
      // Lock the step back until the delete contract lands (button is disabled
      // to match).
      if (challengesPersisted) return;
      setActiveStep("challenges");
      return;
    }
    if (activeStep === "challenges") {
      setActiveStep("overview");
    }
    // Overview is the first step: its in-card Back is disabled, and exiting the
    // wizard is handled by the page header's back arrow (`handleBack`).
  }, [activeStep, challengesPersisted]);

  const isOverviewValid = name.trim().length > 0;
  const isChallengesValid = hasAtLeastOneChallenge(challenges);
  const isScheduleValid = isMonthlyScheduleValid(schedule);

  // Create the Step 1 draft campaign once, caching its id. Mirrors
  // `CreateSeason.ensureSeasonDraft`: the draft is what Steps 2–3 attach their
  // groups, tasks and real schedule to. Errors surface a toast and return
  // `null` so the caller keeps the admin on the current step.
  const ensureCampaignDraft = useCallback(async (): Promise<string | null> => {
    if (campaignId) return campaignId;
    try {
      const created = await createCampaign.mutateAsync(
        buildMonthlyDraftCampaignRequest(name),
      );
      const id = created.campaign_id ?? created.id ?? null;
      if (!id) {
        throw new Error("Campaign create returned no id");
      }
      setCampaignId(id);
      return id;
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.MONTHLY_CHALLENGE_CREATE_ERROR),
      );
      return null;
    }
  }, [campaignId, createCampaign, name]);

  // Persist each challenge card's group + tiered task under the draft campaign.
  // Idempotent per card id (via `persistedCardsRef`) so a retry after a partial
  // failure resumes rather than duplicating already-created records. Editing a
  // card after it has been persisted does not re-sync — TODO(FE follow-up): add
  // update/delete once the admin campaign challenge/task edit contract lands.
  const persistChallengeGroupsAndTasks = useCallback(
    async (id: string): Promise<void> => {
      const cards = challenges.challenges;
      for (let index = 0; index < cards.length; index += 1) {
        const card = cards[index];
        let entry = persistedCardsRef.current.get(card.id);
        if (!entry) {
          const group = await createChallenge.mutateAsync(
            mapMonthlyChallengeToChallengeRequest(card, id, index),
          );
          entry = {
            challengeId: group?.challenge_id ?? group?.id ?? null,
            taskDone: false,
          };
          persistedCardsRef.current.set(card.id, entry);
        }
        if (!entry.taskDone) {
          await createTask.mutateAsync(
            mapMonthlyChallengeToTaskRequest(card, {
              campaignId: id,
              challengeId: entry.challengeId,
              challengeName: card.name,
              challengeOrder: 0,
            }),
          );
          entry.taskDone = true;
        }
      }
      // Campaign-level `streak_reset_hour` is owned by the Schedule-step
      // handlers (`handleCreate` / `handleSaveAsDraft`), not here — this helper
      // already stamps the hour on the task via `mapMonthlyChallengeToTaskRequest`.
      // Anything committed to the backend locks the Challenges step from
      // further edits (no delete API yet — see `persistedCardsRef`).
      if (persistedCardsRef.current.size > 0) {
        setChallengesPersisted(true);
      }
    },
    [challenges.challenges, createChallenge, createTask],
  );

  // Commit the Challenges step — create the groups + tiered tasks under the
  // draft as the admin leaves Step 2 (mirrors Create Season persisting tasks in
  // the flow rather than all at the end). Spins the CTA and toasts on failure;
  // returns whether it succeeded so the caller only advances on success.
  const commitChallengesStep = useCallback(
    async (id: string): Promise<boolean> => {
      setSubmittingAction("next");
      try {
        await persistChallengeGroupsAndTasks(id);
        return true;
      } catch (error) {
        message.error(
          getApiErrorMessage(error, AppStrings.MONTHLY_CHALLENGE_CREATE_ERROR),
        );
        return false;
      } finally {
        setSubmittingAction(null);
      }
    },
    [persistChallengeGroupsAndTasks],
  );

  const handleStepClick = useCallback(
    async (stepId: MonthlyChallengeStepId) => {
      if (stepId === activeStep || submittingAction) return;
      if (stepId === "overview") {
        setActiveStep("overview");
        return;
      }
      // Forward jumps unlock only once the earlier steps are valid, and — like
      // Create Season — leaving Overview first creates the draft campaign so
      // Steps 2–3 have an id to attach to.
      if (stepId === "challenges" && isOverviewValid) {
        // Locked once persisted: re-entering Step 2 could orphan a committed
        // card via delete (mirrors the disabled step-back on Schedule).
        if (challengesPersisted) return;
        let id = campaignId;
        if (!id) {
          setSubmittingAction("next");
          id = await ensureCampaignDraft();
          setSubmittingAction(null);
          if (!id) return;
        }
        setActiveStep("challenges");
        return;
      }
      if (stepId === "schedule" && isOverviewValid && isChallengesValid) {
        let id = campaignId;
        if (!id) {
          setSubmittingAction("next");
          id = await ensureCampaignDraft();
          setSubmittingAction(null);
          if (!id) return;
        }
        // A stepper jump to Schedule commits the groups + tasks just like the
        // Challenges-step Next does.
        if (!(await commitChallengesStep(id))) return;
        setActiveStep("schedule");
      }
    },
    [
      activeStep,
      campaignId,
      challengesPersisted,
      commitChallengesStep,
      ensureCampaignDraft,
      isChallengesValid,
      isOverviewValid,
      submittingAction,
    ],
  );

  // Final "Create" CTA on the Schedule step. The draft campaign and its groups
  // + tiered tasks already exist (created on Steps 1–2), so this writes the
  // admin's real window over the placeholder then transitions the campaign out
  // of `draft` via the schedule API. `persistChallengeGroupsAndTasks` runs first
  // as an idempotent safety net — it no-ops for cards already persisted on Step
  // 2 and only creates ones that slipped through.
  const handleCreate = useCallback(async () => {
    if (!isScheduleValid || submittingAction) return;
    if (!campaignId) {
      message.error(AppStrings.MONTHLY_CHALLENGE_CREATE_ERROR);
      return;
    }
    // Block a window that overlaps an existing active/scheduled campaign
    // (mirrors Create Season's schedule guard). Draft / completed / cancelled
    // campaigns don't block, and the draft being scheduled is excluded so it
    // never conflicts with itself. Save as Draft intentionally skips this — a
    // draft holds no committed window (same as Seasons).
    const conflict = findCampaignScheduleConflict({
      schedule,
      existingCampaigns: campaignsListQuery.data?.campaigns ?? [],
      excludeCampaignId: campaignId,
    });
    if (conflict) {
      message.error(buildCampaignScheduleConflictMessage(conflict));
      return;
    }
    setSubmittingAction("next");
    try {
      await persistChallengeGroupsAndTasks(campaignId);
      const body = {
        ...mapMonthlyScheduleToUpdateCampaignRequest(schedule),
        ...buildStreakResetHourPatch(challenges.challenges),
      };
      if (Object.keys(body).length > 0) {
        await updateCampaign.mutateAsync({ campaignId, body });
      }
      await scheduleCampaign.mutateAsync(campaignId);
      message.success(AppStrings.MONTHLY_CHALLENGE_CREATE_SUCCESS);
      navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_MONTHLY);
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.MONTHLY_CHALLENGE_CREATE_ERROR),
      );
    } finally {
      setSubmittingAction(null);
    }
  }, [
    campaignId,
    campaignsListQuery.data,
    isScheduleValid,
    navigate,
    persistChallengeGroupsAndTasks,
    schedule,
    scheduleCampaign,
    submittingAction,
    updateCampaign,
    challenges.challenges,
  ]);

  const handleNext = useCallback(async () => {
    if (activeStep === "overview") {
      if (!isOverviewValid || submittingAction) return;
      setSubmittingAction("next");
      const id = await ensureCampaignDraft();
      setSubmittingAction(null);
      if (!id) return;
      setActiveStep("challenges");
      return;
    }
    if (activeStep === "challenges") {
      if (!isChallengesValid || submittingAction || !campaignId) return;
      // Create the challenge groups + tiered tasks now (on the Challenges-step
      // Next), matching Create Season's per-step persistence; only advance once
      // they're saved.
      if (!(await commitChallengesStep(campaignId))) return;
      setActiveStep("schedule");
      return;
    }
    // "schedule" is the last step: the primary CTA becomes "Create". It owns its
    // own async error handling.
    void handleCreate();
  }, [
    activeStep,
    campaignId,
    commitChallengesStep,
    ensureCampaignDraft,
    handleCreate,
    isChallengesValid,
    isOverviewValid,
    submittingAction,
  ]);

  // "Save as Draft" saves the admin's in-progress work at every step (mirrors
  // Create Season), never scheduling the campaign:
  //  • Overview — create the draft campaign.
  //  • Challenges — create the campaign draft (if needed) + persist the
  //    challenge groups + tiered tasks added so far, so they aren't lost.
  //  • Schedule — same as Challenges, plus write the entered window over the
  //    Step 1 placeholder.
  const handleSaveAsDraft = useCallback(async () => {
    if (submittingAction) return;
    if (activeStep === "overview" && !isOverviewValid) return;

    setSubmittingAction("draft");
    try {
      const id = await ensureCampaignDraft();
      if (!id) return;
      // Persist any challenge groups + tasks the admin has added (idempotent, so
      // this never duplicates ones already saved). No-ops on Overview where the
      // list is still empty.
      await persistChallengeGroupsAndTasks(id);
      if (activeStep === "schedule") {
        const body = {
          ...mapMonthlyScheduleToUpdateCampaignRequest(schedule),
          ...buildStreakResetHourPatch(challenges.challenges),
        };
        if (Object.keys(body).length > 0) {
          await updateCampaign.mutateAsync({ campaignId: id, body });
        }
      }
      navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_MONTHLY);
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.MONTHLY_CHALLENGE_CREATE_ERROR),
      );
    } finally {
      setSubmittingAction(null);
    }
  }, [
    activeStep,
    ensureCampaignDraft,
    isOverviewValid,
    navigate,
    persistChallengeGroupsAndTasks,
    schedule,
    submittingAction,
    updateCampaign,
    challenges.challenges,
  ]);

  // Both footer CTAs lock while any create/update/schedule request is in flight
  // so the sequence can't double-fire; the spinner sits on whichever button
  // fired it (`submittingAction`).
  const isSubmitting = submittingAction !== null;
  // The Schedule step's Create CTA needs the campaigns list to run the overlap
  // guard, so keep it disabled until the list resolves (mirrors Create Season's
  // `isScheduleStepBlockedByList`). A failed list load blocks too rather than
  // letting a conflicting window through unchecked.
  const isScheduleStepBlockedByList =
    activeStep === "schedule" &&
    (campaignsListQuery.isLoading || campaignsListQuery.isError);
  const isNextDisabled = (() => {
    if (activeStep === "overview") return !isOverviewValid || isSubmitting;
    if (activeStep === "challenges") return !isChallengesValid || isSubmitting;
    return !isScheduleValid || isSubmitting || isScheduleStepBlockedByList;
  })();
  const isSaveDisabled =
    (activeStep === "overview" && !isOverviewValid) || isSubmitting;

  return (
    <>
      <div className={styles.page} data-testid="monthly-challenge">
        <WizardPageHeader
          title={AppStrings.MONTHLY_CHALLENGE_PAGE_TITLE}
          backAriaLabel={AppStrings.MONTHLY_CHALLENGE_BACK_BUTTON_ARIA}
          onBack={handleBack}
          backTestId="monthly-challenge-back"
        />

        <main className={styles.wizardCard}>
          <div className={styles.wizardHeader}>
            <button
              type="button"
              className={styles.wizardHeaderAction}
              onClick={handleHeaderBack}
              disabled={
                activeStep === "overview" ||
                (activeStep === "schedule" && challengesPersisted)
              }
              data-testid="monthly-challenge-step-back"
            >
              <LeftOutlined className={styles.wizardHeaderActionIcon} />
              <span>{AppStrings.BACK}</span>
            </button>

            <WizardStepper
              steps={MONTHLY_CHALLENGE_STEPS}
              activeStepId={activeStep}
              ariaLabel={AppStrings.MONTHLY_CHALLENGE_STEPS_ARIA}
              idPrefix="monthly-challenge-step"
              panelIdPrefix={STEP_PANEL_ID_PREFIX}
              onStepClick={handleStepClick}
            />

            {activeStep === "schedule" ? (
              // The Schedule step's forward action is the footer "Create
              // Monthly Challenge" CTA, so the in-header Next is hidden here
              // (matches the Figma). The empty grid cell keeps the stepper
              // centred.
              <span aria-hidden="true" />
            ) : (
              <button
                type="button"
                className={styles.wizardHeaderActionRight}
                onClick={handleNext}
                disabled={isNextDisabled}
                data-testid="monthly-challenge-step-next"
              >
                <span>{AppStrings.NEXT}</span>
                <RightOutlined className={styles.wizardHeaderActionIcon} />
              </button>
            )}
          </div>

          {activeStep === "overview" ? (
            <div
              id={`${STEP_PANEL_ID_PREFIX}-overview`}
              role="tabpanel"
              aria-labelledby="monthly-challenge-step-overview"
              className={styles.stepPanel}
              data-testid="monthly-challenge-step-panel-overview"
            >
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  {`${AppStrings.MONTHLY_CHALLENGE_NAME_LABEL}*`}
                </span>
                <Input
                  value={name}
                  placeholder={AppStrings.MONTHLY_CHALLENGE_NAME_PLACEHOLDER}
                  onChange={(e) => setName(e.target.value)}
                  // Once the Step 1 draft exists the name is locked (the draft
                  // create doesn't persist further edits) — mirrors Create
                  // Season disabling Overview after the draft is created.
                  disabled={campaignId !== null}
                  data-testid="monthly-challenge-name"
                />
              </label>
            </div>
          ) : null}

          {activeStep === "challenges" ? (
            <div
              id={`${STEP_PANEL_ID_PREFIX}-challenges`}
              role="tabpanel"
              aria-labelledby="monthly-challenge-step-challenges"
              className={styles.stepPanel}
              data-testid="monthly-challenge-step-panel-challenges"
            >
              <MonthlyChallengesStep
                values={challenges}
                onChange={setChallenges}
              />
            </div>
          ) : null}

          {activeStep === "schedule" ? (
            <div
              id={`${STEP_PANEL_ID_PREFIX}-schedule`}
              role="tabpanel"
              aria-labelledby="monthly-challenge-step-schedule"
              className={styles.stepPanel}
              data-testid="monthly-challenge-step-panel-schedule"
            >
              <MonthlySchedule
                values={schedule}
                onValuesChange={setSchedule}
                autoStartAnchor={autoStartAnchor}
              />
            </div>
          ) : null}

          <div className={styles.footer}>
            <Button
              intent="neutral"
              variant="outline"
              onClick={handleSaveAsDraft}
              disabled={isSaveDisabled}
              loading={submittingAction === "draft"}
              data-testid="monthly-challenge-save-draft"
            >
              {AppStrings.SAVE_AS_DRAFT}
            </Button>
            <Button
              intent="primary"
              onClick={handleNext}
              disabled={isNextDisabled}
              loading={submittingAction === "next"}
              data-testid="monthly-challenge-next"
            >
              {activeStep === "schedule"
                ? AppStrings.MONTHLY_CHALLENGE_CREATE_CTA
                : AppStrings.NEXT}
            </Button>
          </div>
        </main>
      </div>
    </>
  );
};

export default MonthlyChallenge;
